import { Language } from '../types';

export interface PlanningPokerCardTranslations {
  zero: { name: string; description: string };
  half: { name: string; description: string };
  one: { name: string; description: string };
  two: { name: string; description: string };
  three: { name: string; description: string };
  five: { name: string; description: string };
  eight: { name: string; description: string };
  thirteen: { name: string; description: string };
  twenty: { name: string; description: string };
  forty: { name: string; description: string };
  hundred: { name: string; description: string };
  question: { name: string; description: string };
  coffee: { name: string; description: string };
}

export interface UITranslations {
  home: {
    title: string;
    subtitle: string;
    createRoomTitle: string;
    createRoomDescription: string;
    createRoom: string;
    joinRoomTitle: string;
    joinRoomDescription: string;
    joinRoom: string;
    whatIsTitle: string;
    description1: string;
    description2: string;
  };
  createRoom: {
    title: string;
    subtitle: string;
    roomName: string;
    roomNamePlaceholder: string;
    playerName: string;
    playerNamePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    create: string;
    cancel: string;
  };
  joinRoom: {
    title: string;
    subtitle: string;
    roomCode: string;
    roomCodePlaceholder: string;
    playerName: string;
    playerNamePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    join: string;
    cancel: string;
  };
  room: {
    title: string;
    situations: string;
    addSituation: string;
    situationTitle: string;
    situationTitlePlaceholder: string;
    status: string;
    actions: string;
    startVoting: string;
    removeSituation: string;
    open: string;
    voting: string;
    closed: string;
    votingEndsIn: string;
    selectCard: string;
    voted: string;
    votingResults: string;
    votes: string;
    noSituations: string;
    addFirstSituation: string;
    players: string;
  };
}

export const planningPokerCardTranslations: Record<Language, PlanningPokerCardTranslations> = {
  en: {
    zero: { name: '0', description: 'No effort needed' },
    half: { name: '½', description: 'Trivial task' },
    one: { name: '1', description: 'Very small task' },
    two: { name: '2', description: 'Small task' },
    three: { name: '3', description: 'Small-medium task' },
    five: { name: '5', description: 'Medium task' },
    eight: { name: '8', description: 'Large task' },
    thirteen: { name: '13', description: 'Very large task' },
    twenty: { name: '20', description: 'Huge task' },
    forty: { name: '40', description: 'Epic task' },
    hundred: { name: '100', description: 'Massive task' },
    question: { name: '?', description: 'Need more info' },
    coffee: { name: '☕', description: 'Need a break!' },
  },
  pt: {
    zero: { name: '0', description: 'Sem esforço necessário' },
    half: { name: '½', description: 'Tarefa trivial' },
    one: { name: '1', description: 'Tarefa muito pequena' },
    two: { name: '2', description: 'Tarefa pequena' },
    three: { name: '3', description: 'Tarefa pequena-média' },
    five: { name: '5', description: 'Tarefa média' },
    eight: { name: '8', description: 'Tarefa grande' },
    thirteen: { name: '13', description: 'Tarefa muito grande' },
    twenty: { name: '20', description: 'Tarefa enorme' },
    forty: { name: '40', description: 'Tarefa épica' },
    hundred: { name: '100', description: 'Tarefa massiva' },
    question: { name: '?', description: 'Precisa de mais informações' },
    coffee: { name: '☕', description: 'Precisa de uma pausa!' },
  },
  es: {
    zero: { name: '0', description: 'Sin esfuerzo necesario' },
    half: { name: '½', description: 'Tarea trivial' },
    one: { name: '1', description: 'Tarea muy pequeña' },
    two: { name: '2', description: 'Tarea pequeña' },
    three: { name: '3', description: 'Tarea pequeña-mediana' },
    five: { name: '5', description: 'Tarea mediana' },
    eight: { name: '8', description: 'Tarea grande' },
    thirteen: { name: '13', description: 'Tarea muy grande' },
    twenty: { name: '20', description: 'Tarea enorme' },
    forty: { name: '40', description: 'Tarea épica' },
    hundred: { name: '100', description: 'Tarea masiva' },
    question: { name: '?', description: 'Necesita más información' },
    coffee: { name: '☕', description: '¡Necesita un descanso!' },
  },
  fr: {
    zero: { name: '0', description: 'Aucun effort nécessaire' },
    half: { name: '½', description: 'Tâche triviale' },
    one: { name: '1', description: 'Très petite tâche' },
    two: { name: '2', description: 'Petite tâche' },
    three: { name: '3', description: 'Petite-moyenne tâche' },
    five: { name: '5', description: 'Tâche moyenne' },
    eight: { name: '8', description: 'Grande tâche' },
    thirteen: { name: '13', description: 'Très grande tâche' },
    twenty: { name: '20', description: 'Tâche énorme' },
    forty: { name: '40', description: 'Tâche épique' },
    hundred: { name: '100', description: 'Tâche massive' },
    question: { name: '?', description: "Besoin de plus d'informations" },
    coffee: { name: '☕', description: "Besoin d'une pause !" },
  },
  de: {
    zero: { name: '0', description: 'Kein Aufwand erforderlich' },
    half: { name: '½', description: 'Triviale Aufgabe' },
    one: { name: '1', description: 'Sehr kleine Aufgabe' },
    two: { name: '2', description: 'Kleine Aufgabe' },
    three: { name: '3', description: 'Klein-mittlere Aufgabe' },
    five: { name: '5', description: 'Mittlere Aufgabe' },
    eight: { name: '8', description: 'Große Aufgabe' },
    thirteen: { name: '13', description: 'Sehr große Aufgabe' },
    twenty: { name: '20', description: 'Riesige Aufgabe' },
    forty: { name: '40', description: 'Epische Aufgabe' },
    hundred: { name: '100', description: 'Massive Aufgabe' },
    question: { name: '?', description: 'Mehr Informationen benötigt' },
    coffee: { name: '☕', description: 'Brauche eine Pause!' },
  },
};

export const uiTranslations: Record<Language, UITranslations> = {
  en: {
    home: {
      title: 'Planning Poker',
      subtitle: 'Estimate effort collaboratively with your team using story points',
      createRoomTitle: 'Create a Room',
      createRoomDescription: 'Start a new planning poker session and invite your team',
      createRoom: 'Create Room',
      joinRoomTitle: 'Join a Room',
      joinRoomDescription: 'Join an existing session with a room code',
      joinRoom: 'Join Room',
      whatIsTitle: 'What is Planning Poker?',
      description1:
        'Planning Poker is an agile estimation technique that helps teams estimate the effort required for user stories or tasks. Each team member votes simultaneously using numbered cards, preventing anchoring bias.',
      description2:
        'By playing planning poker, teams can reach consensus on effort estimates through discussion, leading to more accurate sprint planning and better team alignment.',
    },
    createRoom: {
      title: 'Create a Room',
      subtitle: 'Set up a new planning poker session',
      roomName: 'Room Name',
      roomNamePlaceholder: 'Enter room name',
      playerName: 'Your Name',
      playerNamePlaceholder: 'Enter your name',
      email: 'Email',
      emailPlaceholder: 'Enter your email',
      create: 'Create Room',
      cancel: 'Cancel',
    },
    joinRoom: {
      title: 'Join a Room',
      subtitle: 'Join an existing planning poker session',
      roomCode: 'Room Code',
      roomCodePlaceholder: 'Enter room code',
      playerName: 'Your Name',
      playerNamePlaceholder: 'Enter your name',
      email: 'Email',
      emailPlaceholder: 'Enter your email',
      join: 'Join Room',
      cancel: 'Cancel',
    },
    room: {
      title: 'Planning Poker Room',
      situations: 'User Stories',
      addSituation: 'Add User Story',
      situationTitle: 'User Story',
      situationTitlePlaceholder: 'Enter user story description',
      status: 'Status',
      actions: 'Actions',
      startVoting: 'Start Voting',
      removeSituation: 'Remove',
      open: 'Open',
      voting: 'Voting',
      closed: 'Closed',
      votingEndsIn: 'Voting ends in',
      selectCard: 'Select your story point estimate',
      voted: 'You voted',
      votingResults: 'Voting Results',
      votes: 'votes',
      noSituations: 'No user stories yet',
      addFirstSituation: 'Add your first user story to start planning poker',
      players: 'Players',
    },
  },
  pt: {
    home: {
      title: 'Planning Poker',
      subtitle: 'Estime o esforço colaborativamente com sua equipe usando story points',
      createRoomTitle: 'Criar uma Sala',
      createRoomDescription: 'Inicie uma nova sessão de planning poker e convide sua equipe',
      createRoom: 'Criar Sala',
      joinRoomTitle: 'Entrar em uma Sala',
      joinRoomDescription: 'Entre em uma sessão existente com um código de sala',
      joinRoom: 'Entrar na Sala',
      whatIsTitle: 'O que é Planning Poker?',
      description1:
        'Planning Poker é uma técnica ágil de estimativa que ajuda equipes a estimar o esforço necessário para histórias de usuário ou tarefas. Cada membro da equipe vota simultaneamente usando cartões numerados, evitando viés de ancoragem.',
      description2:
        'Ao jogar planning poker, as equipes podem chegar a um consenso sobre estimativas de esforço através de discussão, levando a um planejamento de sprint mais preciso e melhor alinhamento da equipe.',
    },
    createRoom: {
      title: 'Criar uma Sala',
      subtitle: 'Configure uma nova sessão de planning poker',
      roomName: 'Nome da Sala',
      roomNamePlaceholder: 'Digite o nome da sala',
      playerName: 'Seu Nome',
      playerNamePlaceholder: 'Digite seu nome',
      email: 'Email',
      emailPlaceholder: 'Digite seu email',
      create: 'Criar Sala',
      cancel: 'Cancelar',
    },
    joinRoom: {
      title: 'Entrar em uma Sala',
      subtitle: 'Entre em uma sessão existente de planning poker',
      roomCode: 'Código da Sala',
      roomCodePlaceholder: 'Digite o código da sala',
      playerName: 'Seu Nome',
      playerNamePlaceholder: 'Digite seu nome',
      email: 'Email',
      emailPlaceholder: 'Digite seu email',
      join: 'Entrar na Sala',
      cancel: 'Cancelar',
    },
    room: {
      title: 'Sala de Planning Poker',
      situations: 'Histórias de Usuário',
      addSituation: 'Adicionar História',
      situationTitle: 'História de Usuário',
      situationTitlePlaceholder: 'Descreva a história de usuário',
      status: 'Status',
      actions: 'Ações',
      startVoting: 'Iniciar Votação',
      removeSituation: 'Remover',
      open: 'Aberta',
      voting: 'Votando',
      closed: 'Fechada',
      votingEndsIn: 'Votação termina em',
      selectCard: 'Selecione sua estimativa em story points',
      voted: 'Você votou',
      votingResults: 'Resultados da Votação',
      votes: 'votos',
      noSituations: 'Nenhuma história de usuário ainda',
      addFirstSituation: 'Adicione sua primeira história de usuário para começar o planning poker',
      players: 'Jogadores',
    },
  },
  es: {
    home: {
      title: 'Planning Poker',
      subtitle: 'Estima el esfuerzo colaborativamente con tu equipo usando story points',
      createRoomTitle: 'Crear una Sala',
      createRoomDescription: 'Inicia una nueva sesión de planning poker e invita a tu equipo',
      createRoom: 'Crear Sala',
      joinRoomTitle: 'Unirse a una Sala',
      joinRoomDescription: 'Únete a una sesión existente con un código de sala',
      joinRoom: 'Unirse a la Sala',
      whatIsTitle: '¿Qué es Planning Poker?',
      description1:
        'Planning Poker es una técnica ágil de estimación que ayuda a los equipos a estimar el esfuerzo requerido para historias de usuario o tareas. Cada miembro del equipo vota simultáneamente usando tarjetas numeradas, previniendo el sesgo de anclaje.',
      description2:
        'Al jugar planning poker, los equipos pueden llegar a un consenso sobre las estimaciones de esfuerzo a través de la discusión, lo que lleva a una planificación de sprint más precisa y mejor alineación del equipo.',
    },
    createRoom: {
      title: 'Crear una Sala',
      subtitle: 'Configura una nueva sesión de planning poker',
      roomName: 'Nombre de la Sala',
      roomNamePlaceholder: 'Ingresa el nombre de la sala',
      playerName: 'Tu Nombre',
      playerNamePlaceholder: 'Ingresa tu nombre',
      email: 'Email',
      emailPlaceholder: 'Ingresa tu email',
      create: 'Crear Sala',
      cancel: 'Cancelar',
    },
    joinRoom: {
      title: 'Unirse a una Sala',
      subtitle: 'Únete a una sesión existente de planning poker',
      roomCode: 'Código de Sala',
      roomCodePlaceholder: 'Ingresa el código de sala',
      playerName: 'Tu Nombre',
      playerNamePlaceholder: 'Ingresa tu nombre',
      email: 'Email',
      emailPlaceholder: 'Ingresa tu email',
      join: 'Unirse a la Sala',
      cancel: 'Cancelar',
    },
    room: {
      title: 'Sala de Planning Poker',
      situations: 'Historias de Usuario',
      addSituation: 'Añadir Historia',
      situationTitle: 'Historia de Usuario',
      situationTitlePlaceholder: 'Describe la historia de usuario',
      status: 'Estado',
      actions: 'Acciones',
      startVoting: 'Iniciar Votación',
      removeSituation: 'Eliminar',
      open: 'Abierta',
      voting: 'Votando',
      closed: 'Cerrada',
      votingEndsIn: 'La votación termina en',
      selectCard: 'Selecciona tu estimación en story points',
      voted: 'Has votado',
      votingResults: 'Resultados de la Votación',
      votes: 'votos',
      noSituations: 'No hay historias de usuario aún',
      addFirstSituation: 'Añade tu primera historia de usuario para comenzar el planning poker',
      players: 'Jugadores',
    },
  },
  fr: {
    home: {
      title: 'Planning Poker',
      subtitle: "Estimez l'effort collaborativement avec votre équipe en utilisant des story points",
      createRoomTitle: 'Créer une Salle',
      createRoomDescription: 'Démarrez une nouvelle session de planning poker et invitez votre équipe',
      createRoom: 'Créer une Salle',
      joinRoomTitle: 'Rejoindre une Salle',
      joinRoomDescription: 'Rejoignez une session existante avec un code de salle',
      joinRoom: 'Rejoindre la Salle',
      whatIsTitle: "Qu'est-ce que Planning Poker ?",
      description1:
        "Planning Poker est une technique d'estimation agile qui aide les équipes à estimer l'effort requis pour les histoires utilisateur ou les tâches. Chaque membre de l'équipe vote simultanément en utilisant des cartes numérotées, évitant ainsi le biais d'ancrage.",
      description2:
        "En jouant au planning poker, les équipes peuvent atteindre un consensus sur les estimations d'effort grâce à la discussion, conduisant à une planification de sprint plus précise et à un meilleur alignement de l'équipe.",
    },
    createRoom: {
      title: 'Créer une Salle',
      subtitle: 'Configurez une nouvelle session de planning poker',
      roomName: 'Nom de la Salle',
      roomNamePlaceholder: 'Entrez le nom de la salle',
      playerName: 'Votre Nom',
      playerNamePlaceholder: 'Entrez votre nom',
      email: 'Email',
      emailPlaceholder: 'Entrez votre email',
      create: 'Créer la Salle',
      cancel: 'Annuler',
    },
    joinRoom: {
      title: 'Rejoindre une Salle',
      subtitle: 'Rejoignez une session existante de planning poker',
      roomCode: 'Code de Salle',
      roomCodePlaceholder: 'Entrez le code de salle',
      playerName: 'Votre Nom',
      playerNamePlaceholder: 'Entrez votre nom',
      email: 'Email',
      emailPlaceholder: 'Entrez votre email',
      join: 'Rejoindre la Salle',
      cancel: 'Annuler',
    },
    room: {
      title: 'Salle de Planning Poker',
      situations: 'Histoires Utilisateur',
      addSituation: 'Ajouter une Histoire',
      situationTitle: 'Histoire Utilisateur',
      situationTitlePlaceholder: "Décrivez l'histoire utilisateur",
      status: 'Statut',
      actions: 'Actions',
      startVoting: 'Démarrer le Vote',
      removeSituation: 'Supprimer',
      open: 'Ouverte',
      voting: 'Vote en cours',
      closed: 'Fermée',
      votingEndsIn: 'Le vote se termine dans',
      selectCard: 'Sélectionnez votre estimation en story points',
      voted: 'Vous avez voté',
      votingResults: 'Résultats du Vote',
      votes: 'votes',
      noSituations: 'Aucune histoire utilisateur pour le moment',
      addFirstSituation: 'Ajoutez votre première histoire utilisateur pour commencer le planning poker',
      players: 'Joueurs',
    },
  },
  de: {
    home: {
      title: 'Planning Poker',
      subtitle: 'Schätzen Sie den Aufwand gemeinsam mit Ihrem Team mithilfe von Story Points',
      createRoomTitle: 'Einen Raum Erstellen',
      createRoomDescription: 'Starten Sie eine neue Planning Poker-Sitzung und laden Sie Ihr Team ein',
      createRoom: 'Raum Erstellen',
      joinRoomTitle: 'Einem Raum Beitreten',
      joinRoomDescription: 'Treten Sie einer bestehenden Sitzung mit einem Raumcode bei',
      joinRoom: 'Raum Beitreten',
      whatIsTitle: 'Was ist Planning Poker?',
      description1:
        'Planning Poker ist eine agile Schätztechnik, die Teams hilft, den für User Stories oder Aufgaben erforderlichen Aufwand zu schätzen. Jedes Teammitglied stimmt gleichzeitig mit nummerierten Karten ab und verhindert so Verankerungseffekte.',
      description2:
        'Durch das Spielen von Planning Poker können Teams durch Diskussion einen Konsens über Aufwandsschätzungen erreichen, was zu einer genaueren Sprint-Planung und besserer Team-Ausrichtung führt.',
    },
    createRoom: {
      title: 'Einen Raum Erstellen',
      subtitle: 'Richten Sie eine neue Planning Poker-Sitzung ein',
      roomName: 'Raumname',
      roomNamePlaceholder: 'Geben Sie den Raumnamen ein',
      playerName: 'Ihr Name',
      playerNamePlaceholder: 'Geben Sie Ihren Namen ein',
      email: 'Email',
      emailPlaceholder: 'Geben Sie Ihre Email ein',
      create: 'Raum Erstellen',
      cancel: 'Abbrechen',
    },
    joinRoom: {
      title: 'Einem Raum Beitreten',
      subtitle: 'Treten Sie einer bestehenden Planning Poker-Sitzung bei',
      roomCode: 'Raumcode',
      roomCodePlaceholder: 'Geben Sie den Raumcode ein',
      playerName: 'Ihr Name',
      playerNamePlaceholder: 'Geben Sie Ihren Namen ein',
      email: 'Email',
      emailPlaceholder: 'Geben Sie Ihre Email ein',
      join: 'Raum Beitreten',
      cancel: 'Abbrechen',
    },
    room: {
      title: 'Planning Poker Raum',
      situations: 'User Stories',
      addSituation: 'User Story Hinzufügen',
      situationTitle: 'User Story',
      situationTitlePlaceholder: 'Beschreiben Sie die User Story',
      status: 'Status',
      actions: 'Aktionen',
      startVoting: 'Abstimmung Starten',
      removeSituation: 'Entfernen',
      open: 'Offen',
      voting: 'Abstimmung läuft',
      closed: 'Geschlossen',
      votingEndsIn: 'Die Abstimmung endet in',
      selectCard: 'Wählen Sie Ihre Story-Point-Schätzung',
      voted: 'Sie haben abgestimmt',
      votingResults: 'Abstimmungsergebnisse',
      votes: 'Stimmen',
      noSituations: 'Noch keine User Stories',
      addFirstSituation: 'Fügen Sie Ihre erste User Story hinzu, um Planning Poker zu starten',
      players: 'Spieler',
    },
  },
};

export const getCardTranslation = (
  language: Language,
  cardId: string
): { name: string; description: string } => {
  const translations = planningPokerCardTranslations[language];
  return translations[cardId as keyof PlanningPokerCardTranslations] || { name: cardId, description: '' };
};

export const getUITranslation = (language: Language): UITranslations => {
  return uiTranslations[language];
};
